import { ReactNode, memo } from 'react';
import { cn } from '@/lib/utils';

export type AlertVariant = 'success' | 'error' | 'warning' | 'info';

export interface AlertProps {
  variant: AlertVariant;
  title?: string;
  children: ReactNode;
  onClose?: () => void;
  className?: string;
}

/**
 * Alert - Mensajes de alerta con estilos Tailwind Plus
 * 
 * Mejoras aplicadas:
 * - Dark mode: bg-color-500/10 con ring-inset
 * - Colores de ícono ajustados para dark mode
 * - Mejor contraste de texto
 */
const variantClasses: Record<AlertVariant, { container: string; icon: string; title: string; text: string }> = {
  success: {
    container: cn(
      'bg-success-50 border-l-4 border-success-400',
      'dark:bg-success-500/10 dark:border-success-500/50',
      'dark:ring-1 dark:ring-inset dark:ring-success-500/15'
    ),
    icon: 'text-success-400 dark:text-success-300',
    title: 'text-success-800 dark:text-success-100',
    text: 'text-success-700 dark:text-success-100/80',
  },
  error: {
    container: cn(
      'bg-danger-50 border-l-4 border-danger-400',
      'dark:bg-danger-500/10 dark:border-danger-500/50',
      'dark:ring-1 dark:ring-inset dark:ring-danger-500/15'
    ),
    icon: 'text-danger-400 dark:text-danger-300',
    title: 'text-danger-800 dark:text-danger-100',
    text: 'text-danger-700 dark:text-danger-100/80',
  },
  warning: {
    container: cn(
      'bg-warning-50 border-l-4 border-warning-400',
      'dark:bg-warning-500/10 dark:border-warning-500/50',
      'dark:ring-1 dark:ring-inset dark:ring-warning-500/15'
    ),
    icon: 'text-warning-400 dark:text-warning-300',
    title: 'text-warning-800 dark:text-warning-100',
    text: 'text-warning-700 dark:text-warning-100/80',
  },
  info: {
    container: cn(
      'bg-info-50 border-l-4 border-info-400',
      'dark:bg-info-500/10 dark:border-info-500/50',
      'dark:ring-1 dark:ring-inset dark:ring-info-500/15'
    ),
    icon: 'text-info-400 dark:text-info-300',
    title: 'text-info-800 dark:text-info-100',
    text: 'text-info-700 dark:text-info-100/80',
  },
};

const icons: Record<AlertVariant, string> = {
  success: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
  error: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z',
  warning: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
  info: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
};

export const Alert = memo(function Alert({ variant, title, children, onClose, className }: AlertProps) {
  const classes = variantClasses[variant];
  const iconPath = icons[variant];

  return (
    <div className={cn('rounded-md p-4', classes.container, className)} role="alert">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className={cn('h-5 w-5', classes.icon)} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d={iconPath} clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={cn('text-sm font-medium mb-1', classes.title)}>{title}</h3>
          )}
          <div className={cn('text-sm', classes.text)}>{children}</div>
        </div>
        {onClose && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'inline-flex rounded-md hover:opacity-75',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500',
                'dark:focus-visible:ring-offset-secondary-900',
                classes.text
              )}
              aria-label="Close"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
});
